/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { RocketIcon } from '@radix-ui/react-icons';
import { Button, Text } from '@/shared/ui';
import { fetchAgencyPublications } from '@/features/publications-catalog/api/publications-catalog-api';
import { isSocialPortal } from '@/features/publications-catalog/types';
import { PropertyPicker } from '@/features/partnerships/components/PropertyPicker';

interface FirstPostPromptProps {
  agencyId: string;
  t: (key: string) => string;
}

/**
 * Empty-state nudge shown once Facebook/Instagram is connected but the
 * agency has never actually published anything to either network yet.
 * Opens a property picker; selecting one drops the user straight into that
 * property's Publicação tab (with the publish dialog already open) instead
 * of making them find their way there on their own.
 *
 * Renders nothing while loading or once a social publication exists --
 * this is strictly a one-time onboarding nudge, not a persistent banner.
 */
export const FirstPostPrompt: React.FC<FirstPostPromptProps> = ({ agencyId, t }) => {
  const navigate = useNavigate();
  const [hasSocialPost, setHasSocialPost] = useState<boolean | null>(null);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAgencyPublications(agencyId)
      .then((res) => {
        if (cancelled) return;
        const hasAny = res.entries.some((entry) => isSocialPortal(entry.portal) && entry.status !== 'error');
        setHasSocialPost(hasAny);
      })
      .catch(() => {
        // Best-effort nudge: if the catalog fetch fails, just stay quiet
        // rather than risk showing the CTA to someone who already posted.
        if (!cancelled) setHasSocialPost(true);
      });
    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  if (hasSocialPost !== false) return null;

  return (
    <>
      <div
        data-testid="first-post-prompt"
        className="mt-(--space-1) pt-(--space-3) border-t border-(--border-subtle) flex items-center justify-between gap-(--space-3)"
      >
        <div className="flex items-center gap-(--space-3)">
          <Text component="span" className="flex text-[var(--seed-base)]" aria-hidden="true">
            <RocketIcon width={18} height={18} />
          </Text>
          <div>
            <Text className="m-0 text-sm font-semibold text-(--text-primary)">
              {t('facebook.firstPost.sectionTitle')}
            </Text>
            <Text className="m-0 mt-(--space-0-5) text-xs text-(--text-secondary)">
              {t('facebook.firstPost.description')}
            </Text>
          </div>
        </div>
        <Button size="xs" data-testid="first-post-cta" className="shrink-0" onClick={() => setShowPicker(true)}>
          {t('facebook.firstPost.cta')}
        </Button>
      </div>

      {showPicker && (
        <PropertyPicker
          agencyId={agencyId}
          onClose={() => setShowPicker(false)}
          onSelect={(property) => {
            const propertyId = (property as { id?: string }).id;
            if (!propertyId) return;
            setShowPicker(false);
            navigate(`/app/agencies/${agencyId}/properties/${propertyId}?tab=publications&openPublish=1`);
          }}
        />
      )}
    </>
  );
};
